import { setDefaultOptions } from 'date-fns';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { languageByCountryCode } from '@/shared/languageCodes';

import { resolveDateFnsLocale } from './dateFnsLocales';
import { formatLanguageCodes } from './helper';

export const DETECTION_OPTIONS = {
  order: ['localStorage', 'navigator'], // Added 'navigator' as a fallback detection method
  caches: ['localStorage'],
  lookupLocalStorage: 'moveoLanguage',
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    load: 'currentOnly',
    ns: ['common', 'languages', 'timezones', 'onboarding'],
    defaultNS: 'common',
    fallbackNS: ['common'],
    // Normalize navigator.language to lowercase with hyphens (e.g., pt-BR → pt-br)
    preload: [
      localStorage.getItem('moveoLanguage') || navigator.language.toLowerCase().replace('_', '-'),
    ],
    // Support all brain languages - UI will gracefully fall back to English for unsupported languages
    supportedLngs: Object.keys(languageByCountryCode),
    detection: DETECTION_OPTIONS,
    lowerCaseLng: true,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV !== 'production',
    backend: {
      loadPath: function (lng: string[], ns: string) {
        // Extract first language from array to avoid invalid paths like /locales/en,en/common.json
        const formattedLng = formatLanguageCodes(lng)[0];

        return `/locales/${formattedLng}/${ns}.json`;
      },
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
  });

// Changes the date-fns locale to adapt to the current language.
i18n.on('languageChanged', function (language) {
  setDefaultOptions({ locale: resolveDateFnsLocale(language) });
});

// Mirror the active locale onto window._language so the fetcher can send it as
// Accept-Language on every outbound API call. Source of truth is i18n.language.
const syncAcceptLanguage = (language: string) => {
  window._language = language;
};
syncAcceptLanguage(i18n.language);
i18n.on('languageChanged', syncAcceptLanguage);

export default i18n;
